import {previewTrimSpecification, TrimSpecificationRequest, type AccessoryFloorProduct, type FloorAccessoryType} from '@artemis-group/aiw-contracts';

/**
 * Mock slice for `/api/pricing-base` — the layout seam for step 2.1 + 2.2.
 *
 * THESE ARE NOT PRICES. Every figure below is a fixture, chosen so the screen can be
 * built and reviewed without a live staff session. The live path is
 * `services/api` → `@artemis-group/domain/engine/proposalBase` and is the only thing
 * that ever prices a real Proposal; nothing in `apps/crm/src` computes money.
 *
 * What it does reproduce, deliberately, is the engine's SHAPE: a line carries a
 * single sell price or a range, a range carries its notice, an undecided colour
 * returns `material: null` (which blanks the internal total exactly as the engine
 * does — the case the staff rail has to explain), a $0 line is still emitted, and
 * services and access sit after the product lines.
 */
const ok = (data: unknown) => new Response(JSON.stringify({ ok: true, data }), { status: 200, headers: { 'content-type': 'application/json' } });
const bad = (error: string) => new Response(JSON.stringify({ error }), { status: 400, headers: { 'content-type': 'application/json' } });

const colour = (productId: string, n: number, name: string, cost: number, over: Record<string, unknown> = {}) => ({
  id: `${productId}-c${n}`, product_id: productId, name, display_name: name, colour: name, line_type: 'Colour',
  label: null, is_default: false, mat_cost: cost, price_per_lm: null, width: null, uom: null, pattern: null, supplier_name: null, ...over,
});

const PRODUCTS = [
  {
    id: 'p-alisa', name: 'Alisa — Signature Floors', type: 'Carpet', sub_type: null, uom: 'LM', supplier_name: 'Signature Floors', standard_cost: 41.8,
    pricing: { cost: 41.8, costRange: { min: 41.8, max: 41.8 }, unit: 'LM', source: 'All colour costs', issues: [], missing: [], manual: false, rule: 'carpet', promotion: false, priceBasis: null, colours: 4 },
    variants: [colour('p-alisa', 1, 'Ash Grey', 41.8), colour('p-alisa', 2, 'Storm', 41.8), colour('p-alisa', 3, 'Sand Dune', 41.8), colour('p-alisa', 4, 'Charcoal', 41.8)],
    colours: [colour('p-alisa', 1, 'Ash Grey', 41.8), colour('p-alisa', 2, 'Storm', 41.8), colour('p-alisa', 3, 'Sand Dune', 41.8), colour('p-alisa', 4, 'Charcoal', 41.8)],
  },
  {
    id: 'p-wonderwood', name: 'Wonderwood Hybrid 9mm', type: 'Hybrid', sub_type: 'Hybrid', uom: 'm²', supplier_id: 'sup-wonderwood', supplier_name: 'Wonderwood', standard_cost: 33.5,
    pricing: { cost: 33.5, costRange: { min: 33.5, max: 33.5 }, unit: 'm²', source: 'All colour costs', issues: [], missing: [], manual: false, rule: 'hybrid', promotion: false, priceBasis: null, colours: 3 },
    variants: [colour('p-wonderwood', 1, 'Natural Oak', 33.5), colour('p-wonderwood', 2, 'Smoked Oak', 33.5), colour('p-wonderwood', 3, 'Grey Ash', 33.5)],
    colours: [colour('p-wonderwood', 1, 'Natural Oak', 33.5), colour('p-wonderwood', 2, 'Smoked Oak', 33.5), colour('p-wonderwood', 3, 'Grey Ash', 33.5)],
  },
  {
    id: 'p-topdeck', name: 'Topdeck Prime Platinum 12.3mm', type: 'Laminate', sub_type: 'Laminate', uom: 'm²', supplier_name: 'Topdeck', standard_cost: 22,
    pricing: { cost: null, costRange: { min: 22, max: 28 }, unit: 'm²', source: 'All colour costs', issues: [], missing: [], manual: false, rule: 'laminate', promotion: false, priceBasis: 'COD', colours: 2 },
    variants: [colour('p-topdeck', 1, 'Platinum Oak', 22), colour('p-topdeck', 2, 'Platinum Walnut', 28)],
    colours: [colour('p-topdeck', 1, 'Platinum Oak', 22), colour('p-topdeck', 2, 'Platinum Walnut', 28)],
  },
  {
    id: 'p-vinyl-sheet', name: 'Vinyl sheet — assessed per job', type: 'Vinyl', sub_type: 'Sheet Vinyl', uom: 'm²', supplier_name: 'Polyflor', standard_cost: null,
    pricing: { cost: null, costRange: null, unit: 'm²', source: 'Manual assessment', issues: [], missing: [], manual: true, rule: null, promotion: false, priceBasis: null, colours: 0 },
    variants: [], colours: [],
  },
];

/**
 * THE ACCESSORY CATALOGUE — fixture, same shape as `/api/pricing-base` sends.
 *
 * Families, the TYPES inside each, and the supplier/spec options inside a type. This is what the
 * switchboard under a product lists; without it the mock has no underlay to choose and no trim to add,
 * which is exactly the fault the live screen had (found on O-00000004, 19 Sep).
 */
const ACCESSORIES = [
  { product_id: 'a-green', kind: 'underlay', name: '2mm Green UD', supplier_name: 'PACO Floors', unit: 'm²', variants: [
    { variant_id: 'a-green-paf-20', label: 'PAF · Underlay - Green (20M)', unit: 'm²', cost: 2, is_default: false },
    { variant_id: 'a-green-paf-50', label: 'PAF · Underlay - Green (50M)', unit: 'm²', cost: 2, is_default: false },
    { variant_id: 'a-green-prf-50', label: 'PRF · Underlay - Green (50M)', unit: 'm²', cost: 3.3, is_default: false },
  ] },
  { product_id: 'a-silver', kind: 'underlay', name: '2mm Silver UD', supplier_name: 'PACO Floors', unit: 'm²', variants: [
    { variant_id: 'a-silver-paf-50', label: 'PAF · Underlay - Silver (50M)', unit: 'm²', cost: 0.9, is_default: false },
    { variant_id: 'a-silver-prf-50', label: 'PRF · Underlay - Silver (50M)', unit: 'm²', cost: 1.3, is_default: false },
  ] },
  { product_id: 'a-rubber', kind: 'underlay', name: 'Rubber Underlay', supplier_name: 'PACO Floors', unit: 'm²', variants: [
    { variant_id: 'a-rubber-5', label: '5mm Rubber Underlay', unit: 'm²', cost: 10, is_default: true },
    { variant_id: 'a-rubber-regupol', label: 'REGUPOL Sonus Core 5 - 5mm', unit: 'm²', cost: 12, is_default: false },
  ] },
  { product_id: 'a-moisture', kind: 'moisture_barrier', name: 'Moisture Barrier', supplier_name: 'PACO Floors', unit: 'm²', variants: [
    { variant_id: 'a-moisture-paf', label: 'PAF · Moisture Barrier', unit: 'm²', cost: 0.5, is_default: false },
    { variant_id: 'a-moisture-prf', label: 'PRF · Moisture Barrier', unit: 'm²', cost: 0.7, is_default: false },
  ] },
  { product_id: 'a-scotia', kind: 'scotia', name: 'Scotia', supplier_name: 'PACO Floors', unit: 'PCs', variants: [
    { variant_id: 'a-scotia-hybrid', label: 'PAF · Scotia · Match Colour', unit: 'PCs', cost: 5.5, is_default: false },
  ] },
  { product_id: 'a-trims-u', kind: 'trims', name: 'Trims - U Channel', supplier_name: 'PACO Floors', unit: 'PCs', variants: [
    { variant_id: 'a-trims-u-8', label: 'PAF · U Channel 8mm', unit: 'PCs', cost: 12, is_default: false },
    { variant_id: 'a-trims-u-12', label: 'PAF · U Channel 12mm', unit: 'PCs', cost: 15, is_default: false },
  ] },
  { product_id: 'a-trims-l', kind: 'trims', name: 'Trims - L Trims', supplier_name: 'PACO Floors', unit: 'PCs', variants: [
    { variant_id: 'a-trims-l-10', label: 'PAF · L trim 10x25mm', unit: 'PCs', cost: 7, is_default: false },
  ] },
];

const TRIM_ACCESSORIES: FloorAccessoryType[] = [{
  product_id: 'a-wonderwood-t', kind: 'trims', name: 'T Trim', supplier_id: 'sup-wonderwood', supplier_name: 'Wonderwood', unit: 'PCs',
  variants: [{variant_id: 'a-wonderwood-t-9-natural', label: 'Wonderwood · T Trim · Natural Oak · 9 mm', unit: 'PCs', cost: 0, is_default: true, supplier_id: 'sup-wonderwood', supplier_name: 'Wonderwood', length_mm: 2400, spec: 'Natural Oak · 9 mm', source: 'mock catalogue'}],
}];

const MODEL = {
  version: 1 as const, currency: 'AUD' as const, gstRate: 0.1, standardMarkup: 0.42, premiumMarkup: 0.5, premiumSuppliers: ['Signature'], supplyMarkup: 0.3,
  rates: [
    { id: 'removal', name: 'Removal of existing floor coverings', unit: 'm²', sell: 12.29, material: 0, labour: 6.5, notes: '' },
    { id: 'disposal', name: 'Disposal of removed floor coverings', unit: 'm²', sell: 9.75, material: 4.2, labour: 2, disposalSell: 9.75, weight: 3.5, dumpingRate: 0.9, notes: '' },
    { id: 'furniture', name: 'Furniture moving', unit: 'job', sell: 300, material: 0, labour: 180, notes: '' },
    { id: 'access-house', name: 'House — site surcharge', unit: 'job', sell: 0, material: 0, labour: null, notes: 'No charge' },
    { id: 'access-elevator', name: 'Lift access', unit: 'job', sell: 95, material: 0, labour: 60, notes: '' },
    { id: 'access-stairs', name: 'Stairs access', unit: 'job', sell: 145, material: 0, labour: 95, notes: '' },
    { id: 'access-above3', name: 'Above three flights', unit: 'job', sell: 260, material: 0, labour: 180, notes: '' },
    { id: 'access-parking', name: 'Unload then drive to park', unit: 'job', sell: 60, material: 0, labour: 0, notes: '' },
    { id: 'access-minimum-house', name: 'Job minimum — house lot', unit: 'job', sell: 1200, material: 0, labour: 0, notes: '' },
  ],
  installation: [
    { id: 'carpet', name: 'Carpet — broadloom, stretched', unit: 'LM' as const, labour: 38, glue: null, glued: false, underlay: 9.4, upgrade: 6.2, markup: 0.45 },
    { id: 'hybrid', name: 'Hybrid — floating', unit: 'm²' as const, labour: 27, glue: null, glued: false, underlay: 0, upgrade: null, markup: 0.4 },
    { id: 'laminate', name: 'Laminate — floating', unit: 'm²' as const, labour: 25, glue: null, glued: false, underlay: 0, upgrade: null, markup: 0.4 },
  ],
};

/** Sell per engine unit, fixture only. */
const SELL: Record<string, { unit: number; range?: { min: number; max: number }; material: number; basis?: string }> = {
  'p-alisa': { unit: 179.64, material: 62.4 },
  'p-wonderwood': { unit: 63.08, material: 33.5 },
  'p-topdeck': { unit: 0, range: { min: 418.47 / 7.8, max: 486.33 / 7.8 }, material: 22, basis: 'COD' },
};

type Selection = { productId: string; variantId?: string; mode: string; quantity: number; area: number; manualSell?: number; sellOverride?:number; accessoryCost?: number; thickerUnderlay?: boolean; shapeConfirmed?: boolean };
type Service = { rateId: string; quantity: number; customerSkip?: boolean };

function priced(input: { products?: Selection[]; services?: Service[]; access?: { kind: string; weightKg: number; unloadParking: boolean }; minimum?: string }) {
  const lines: Record<string, unknown>[] = [];
  const cash = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
  for (const item of input.products ?? []) {
    const product = PRODUCTS.find(p => p.id === item.productId);
    if (!product) return { error: 'Unknown product' };
    if (product.pricing.manual) {
      lines.push({ label: `${product.name} — manual assessment`, unit: 'm²', quantity: item.quantity, revenue: item.manualSell != null ? cash(item.manualSell * item.quantity) : null, material: null, labour: null, warnings: item.manualSell != null ? [] : ['Assessment price required'], notices: [] });
      continue;
    }
    const sell = SELL[product.id]!;
    // An undecided colour on a product whose colours differ in cost is a range; the
    // engine refuses the material total in that case, and so does this fixture.
    const undecided = !item.variantId && product.pricing.costRange != null && product.pricing.costRange.min !== product.pricing.costRange.max;
    const material = undecided ? null : cash(sell.material * item.quantity);
    const labour = item.mode === 'supply' ? 0 : cash((product.pricing.unit === 'LM' ? 38 : 26) * (product.pricing.unit === 'LM' ? item.quantity : item.area));
    const revenue = item.sellOverride!=null?cash(item.sellOverride*item.quantity):undecided ? null : cash(sell.unit * item.quantity);
    lines.push({
      label: product.name, unit: product.pricing.unit, quantity: item.quantity, revenue,
      ...(undecided&&item.sellOverride==null ? { revenueRange: { min: cash(sell.range!.min * item.quantity), max: cash(sell.range!.max * item.quantity) } } : {}),
      material, labour,
      warnings: product.pricing.unit === 'LM' && !item.shapeConfirmed ? ['Dimensioned carpet shape required before estimate'] : [],
      notices: undecided ? ['Final price depends on your selected colour.'] : [],
      ...(product.id === 'p-topdeck' ? { costBasis: 'COD' } : {}),
    });
  }
  for (const item of input.services ?? []) {
    const rate = MODEL.rates.find(r => r.id === item.rateId);
    if (!rate) return { error: 'Unknown service' };
    lines.push({ label: rate.name, unit: rate.unit, quantity: item.quantity, revenue: rate.sell == null ? null : cash(rate.sell * item.quantity), material: rate.material == null ? null : cash(rate.material * item.quantity), labour: rate.labour == null ? null : cash(rate.labour * item.quantity), warnings: rate.sell == null ? ['Selling price required'] : [], notices: [] });
    if (rate.disposalSell != null) lines.push({ label: `${rate.name} — disposal`, unit: 'm²', quantity: item.quantity, revenue: item.customerSkip ? 0 : cash(rate.disposalSell * item.quantity), material: item.customerSkip ? 0 : cash((rate.weight ?? 0) * (rate.dumpingRate ?? 0) * item.quantity), labour: 0, warnings: [], notices: [] });
  }
  if (input.access) {
    const rate = MODEL.rates.find(r => r.id === `access-${input.access!.kind === 'above3' ? 'above3' : input.access!.kind}`);
    const quantity = input.access.kind === 'house' || input.access.kind === 'above3' ? 1 : input.access.weightKg / 10;
    lines.push({ label: rate?.name ?? 'Site access', unit: rate?.unit ?? 'job', quantity, revenue: rate?.sell == null ? null : cash(rate.sell * quantity), material: 0, labour: null, warnings: [], notices: [] });
  }
  const complete = lines.length > 0 && lines.every(l => l.revenue != null);
  const anyRange = lines.every(l => l.revenue != null || l.revenueRange);
  const sum = (pick: (l: Record<string, unknown>) => number | null) => lines.every(l => pick(l) != null) ? cash(lines.reduce((n, l) => n + (pick(l) as number), 0)) : null;
  const subtotal = complete ? cash(lines.reduce((n, l) => n + (l.revenue as number), 0)) : null;
  const total = subtotal == null ? null : cash(subtotal * 1.1);
  const subtotalRange = !complete && anyRange ? {
    min: cash(lines.reduce((n, l) => n + ((l.revenueRange as { min: number } | undefined)?.min ?? (l.revenue as number)), 0)),
    max: cash(lines.reduce((n, l) => n + ((l.revenueRange as { max: number } | undefined)?.max ?? (l.revenue as number)), 0)),
  } : null;
  const settledRange = subtotalRange ? { min: cash(subtotalRange.min * 1.1), max: cash(subtotalRange.max * 1.1) } : null;
  return {
    version: 1,
    gstRate:0.1, subtotalRange, lines,
    warnings: lines.flatMap(l => (l.warnings as string[]).map(w => `${l.label}: ${w}`)),
    subtotal, gst: total == null ? null : cash(total - subtotal!), total, settledRange,
    internal: {
      material: sum(l => l.material as number | null),
      externalLabour: sum(l => l.labour as number | null),
      artemisLabour: 0,
      artemisContribution: subtotal == null || sum(l => l.material as number | null) == null ? null : cash(subtotal - (sum(l => l.material as number | null) as number)),
      externalContribution: null,
    },
  };
}

export const pricingSlice = async (path: string, _q: URLSearchParams, method: string, init?: RequestInit) => {
  if (path === '/api/pricing-base') return ok({ model: MODEL, products: PRODUCTS, accessories: ACCESSORIES,
    // the catalogue's removal floors, as /api/pricing-base sends them (16 Oct 2026)
    removal: [{floor: 'Carpet', variant_id: 'm-carpet', name: 'Remove & Dispose Carpet', sell: 8}, {floor: 'Hybrid', variant_id: 'm-hybrid', name: 'Remove & Dispose Hybrid', sell: 25},
      {floor: 'Laminate', variant_id: 'm-laminate', name: 'Remove & Dispose Laminate', sell: 20}, {floor: 'Tile', variant_id: 'm-tile', name: 'Remove & Dispose existing Tile', sell: 50}] });
  if (path === '/api/trim-specification/preview' && method === 'POST') {
    try {
      const input = TrimSpecificationRequest.parse(JSON.parse(String(init?.body ?? '{}')));
      return ok(previewTrimSpecification(input, PRODUCTS as AccessoryFloorProduct[], TRIM_ACCESSORIES));
    } catch (error) { return bad(error instanceof Error ? error.message : 'Trim specification is unavailable.'); }
  }
  if (path === '/api/pricing-base/preview' && method === 'POST') {
    const body = JSON.parse(String(init?.body ?? '{}')) as Parameters<typeof priced>[0];
    const result = priced(body);
    if ('error' in result) return bad(result.error as string);
    return ok(result);
  }
  return null;
};
